import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Download } from "lucide-react";

import { apiFetch, ApiError } from "@/lib/api";
import {
  controlledDocumentListActiveRevisionLabel,
  controlledDocumentListStatusSummary,
  downloadControlledDocument,
  filenameExtension,
  formatEcoNumber,
  formatRevisionChangeFieldLabel,
  formatTimestamp,
  groupControlledDocumentsForList,
  type ControlledDocument,
  type DocumentChangeOrder,
  type DocumentNote,
  type DocumentRevisionChangeWithDocument,
  type DocumentCollectionSkusResponse,
  type ProductCollection,
  type ProductCollectionEvent,
  type ProductCollectionRelation,
  type ProductCollectionSku,
  type ProductCollectionTemplateSkuExclusion,
  type ProductTransitionNotice,
} from "@/lib/documents";
import { buildMrpSkuDetailPath } from "@/lib/mrp";
import { HoverHintTrigger } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";

function formatHierarchyRelationship(
  currentCollectionId: string,
  relationship: ProductCollectionRelation,
): string {
  if (relationship.relation_type === "part_of") {
    if (relationship.from_collection_id === currentCollectionId) {
      return `Child of ${relationship.to_collection_code} · ${relationship.to_collection_name}`;
    }
    if (relationship.to_collection_id === currentCollectionId) {
      return `Parent of ${relationship.from_collection_code} · ${relationship.from_collection_name}`;
    }
  }
  return `${relationship.from_collection_code} ${relationship.relation_type.replaceAll("_", " ")} ${relationship.to_collection_code}`;
}

function statusClass(status: ControlledDocument["lifecycle_status"]): string {
  if (status === "current") return "bg-green-50 text-green-700";
  if (status === "wip") return "bg-amber-50 text-amber-700";
  if (status === "obsolete") return "bg-slate-100 text-slate-600";
  if (status === "draft") return "bg-blue-50 text-blue-700";
  return "bg-slate-100 text-slate-600";
}

function statusChipLabel(status: ControlledDocument["lifecycle_status"]): string {
  if (status === "wip") return "WIP";
  return status;
}

function ecoStatusClass(status: DocumentChangeOrder["status"]): string {
  if (status === "implemented") return "bg-green-50 text-green-700";
  if (status === "approved") return "bg-blue-50 text-blue-700";
  if (status === "submitted") return "bg-amber-50 text-amber-700";
  if (status === "cancelled") return "bg-red-50 text-red-700";
  return "bg-slate-100 text-slate-700";
}

function transitionStatusClass(status: ProductTransitionNotice["status"]): string {
  if (status === "issued") return "bg-blue-50 text-blue-700";
  if (status === "completed") return "bg-green-50 text-green-700";
  if (status === "cancelled") return "bg-red-50 text-red-700";
  return "bg-slate-100 text-slate-700";
}

type CollectionDetailTab =
  | "change_orders"
  | "transition_notices"
  | "changelog"
  | "related_collections_and_skus"
  | "documents";

const COLLECTION_DETAIL_TABS: { id: CollectionDetailTab; label: string }[] = [
  { id: "change_orders", label: "Change orders" },
  { id: "transition_notices", label: "Transition notices" },
  { id: "changelog", label: "Changelog & activity" },
  { id: "related_collections_and_skus", label: "Related collections and SKUs" },
  { id: "documents", label: "Controlled documents" },
];

interface CollectionDetailBundle {
  collection: ProductCollection;
  skus: ProductCollectionSku[];
  template_sku_exclusions: ProductCollectionTemplateSkuExclusion[];
  relationships: ProductCollectionRelation[];
  events: ProductCollectionEvent[];
  documents: ControlledDocument[];
  changeOrders: DocumentChangeOrder[];
  notes: DocumentNote[];
  revisionChanges: DocumentRevisionChangeWithDocument[];
  transitionNotices: ProductTransitionNotice[];
}

async function fetchCollectionDetail(collectionId: string): Promise<CollectionDetailBundle> {
  const [
    collectionResponse,
    skusResponse,
    relationshipsResponse,
    eventsResponse,
    documentsResponse,
    changeOrdersResponse,
    notesResponse,
    revisionChangesResponse,
    transitionNoticesResponse,
  ] = await Promise.all([
    apiFetch<{ collection: ProductCollection }>(`/api/document-collections/${collectionId}`),
    apiFetch<DocumentCollectionSkusResponse>(`/api/document-collections/${collectionId}/skus`),
    apiFetch<{ relationships: ProductCollectionRelation[] }>(
      `/api/document-collections/${collectionId}/relationships`,
    ),
    apiFetch<{ events: ProductCollectionEvent[] }>(`/api/document-collections/${collectionId}/events`),
    apiFetch<{ documents: ControlledDocument[] }>("/api/documents", {
      query: { productCollectionId: collectionId },
    }),
    apiFetch<{ changeOrders: DocumentChangeOrder[] }>("/api/document-change-orders", {
      query: { productCollectionId: collectionId },
    }),
    apiFetch<{ notes: DocumentNote[] }>("/api/document-notes", {
      query: { productCollectionId: collectionId },
    }),
    apiFetch<{ changes: DocumentRevisionChangeWithDocument[] }>(
      `/api/document-collections/${collectionId}/revision-changes`,
    ),
    apiFetch<{ transitionNotices: ProductTransitionNotice[] }>("/api/document-transition-notices", {
      query: { productCollectionId: collectionId },
    }),
  ]);
  return {
    collection: collectionResponse.collection,
    skus: skusResponse.skus,
    template_sku_exclusions: skusResponse.template_sku_exclusions,
    relationships: relationshipsResponse.relationships,
    events: eventsResponse.events,
    documents: documentsResponse.documents,
    changeOrders: changeOrdersResponse.changeOrders,
    notes: notesResponse.notes,
    revisionChanges: revisionChangesResponse.changes,
    transitionNotices: transitionNoticesResponse.transitionNotices,
  };
}

export function DocumentCollectionDetailPage() {
  const collectionId = useParams<{ collectionId: string }>().collectionId ?? "";
  const [activeTab, setActiveTab] = useState<CollectionDetailTab>("documents");

  const detailQuery = useQuery<CollectionDetailBundle, ApiError>({
    queryKey: ["documents", "collection-detail", collectionId],
    queryFn: () => fetchCollectionDetail(collectionId),
    enabled: collectionId.length > 0,
  });

  const sortedDocumentGroups = useMemo(() => {
    const groups = groupControlledDocumentsForList(detailQuery.data?.documents ?? []);
    return [...groups].sort((groupA, groupB) => {
      const documentA = groupA.representative;
      const documentB = groupB.representative;
      const kindCompare = documentA.document_kind_label.localeCompare(documentB.document_kind_label, undefined, {
        sensitivity: "base",
      });
      if (kindCompare !== 0) return kindCompare;
      return documentA.title.localeCompare(documentB.title, undefined, { sensitivity: "base" });
    });
  }, [detailQuery.data?.documents]);

  if (!collectionId) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-600">Missing collection.</div>
    );
  }

  if (detailQuery.isLoading) {
    return (
      <div className="space-y-6">
        <p className="text-sm text-slate-500">Loading collection…</p>
      </div>
    );
  }

  if (detailQuery.isError) {
    return (
      <div className="space-y-4">
        <HoverHintTrigger
          trigger={
            <Link className="inline-flex items-center gap-2 text-sm font-medium text-brand-700 hover:underline" to="/documents">
              <ArrowLeft className="h-4 w-4" aria-hidden />
              Back to workbench
            </Link>
          }
        >
          Return to Document Control Workbench.
        </HoverHintTrigger>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {detailQuery.error.status === 404
            ? "This product collection was not found."
            : detailQuery.error.message}
        </div>
      </div>
    );
  }

  if (!detailQuery.data) {
    return null;
  }

  const detail = detailQuery.data;
  const collection = detail.collection;

  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <HoverHintTrigger
          trigger={
            <Link className="inline-flex items-center gap-2 text-sm font-medium text-brand-700 hover:underline" to="/documents">
              <ArrowLeft className="h-4 w-4" aria-hidden />
              Back to workbench
            </Link>
          }
        >
          Return to Document Control Workbench.
        </HoverHintTrigger>
        <header>
          <h1 className="text-xl font-semibold text-slate-900">{collection.name}</h1>
          <p className="mt-1 text-sm text-slate-600">
            <span className="font-medium text-slate-800">{collection.code}</span>
            {collection.description ? (
              <>
                {" "}
                · {collection.description}
              </>
            ) : null}
          </p>
          {collection.notes ? <p className="mt-2 text-sm text-slate-600">{collection.notes}</p> : null}
          {collection.bom_template_name ? (
            <p className="mt-2 text-sm text-slate-600">
              Linked BoM template:{" "}
              <span className="font-medium text-slate-800">{collection.bom_template_name}</span>
            </p>
          ) : null}
        </header>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div
          className="flex flex-wrap gap-x-1 gap-y-0 border-b border-slate-200 px-2 pt-3 sm:px-4"
          role="tablist"
          aria-label="Collection sections"
        >
          {COLLECTION_DETAIL_TABS.map((tab) => (
            <HoverHintTrigger
              key={tab.id}
              trigger={
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={cn(
                    "border-b-2 px-3 py-2.5 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600",
                    activeTab === tab.id
                      ? "border-brand-600 text-brand-900"
                      : "border-transparent text-slate-600 hover:border-slate-300 hover:text-slate-900",
                  )}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              }
            >
              {tab.id === "change_orders"
                ? "Engineering change orders that reference documents in this collection."
                : tab.id === "transition_notices"
                  ? "Product transition notices issued for this collection."
                  : tab.id === "changelog"
                    ? "Revision changes, collection events, and notes for this collection."
                    : tab.id === "related_collections_and_skus"
                      ? "Parent and child collection links, member part numbers (explicit and BoM template), and template exclusions."
                      : "Controlled documents whose applicability includes this collection."}
            </HoverHintTrigger>
          ))}
        </div>

        <div className="p-5 sm:p-6" role="tabpanel">
          {activeTab === "change_orders" ? (
            <div>
              <p className="mb-4 text-sm text-slate-600">
                Change orders that include at least one controlled document targeting this collection.
              </p>
              {detail.changeOrders.length === 0 ? (
                <p className="text-sm text-slate-500">No engineering change orders linked through documents yet.</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50">
                      <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                        <th className="px-3 py-2">Number</th>
                        <th className="px-3 py-2">Title</th>
                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2">Documents</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 bg-white">
                      {detail.changeOrders.map((changeOrder) => (
                        <tr key={changeOrder.id}>
                          <td className="whitespace-nowrap px-3 py-3 font-medium text-slate-900">{formatEcoNumber(changeOrder.number)}</td>
                          <td className="max-w-md px-3 py-3 text-slate-700">{changeOrder.title}</td>
                          <td className="whitespace-nowrap px-3 py-3">
                            <span
                              className={`rounded-full px-2 py-1 text-xs font-medium ${ecoStatusClass(changeOrder.status)}`}
                            >
                              {changeOrder.status}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-3 text-slate-600">{changeOrder.document_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}

          {activeTab === "transition_notices" ? (
            <div>
              <p className="mb-4 text-sm text-slate-600">Cutover notices issued for this collection.</p>
              {detail.transitionNotices.length === 0 ? (
                <p className="text-sm text-slate-500">No transition notices for this collection.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {detail.transitionNotices.map((notice) => (
                    <li key={notice.id} className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2">
                      <span
                        className={`mr-2 rounded-full px-2 py-0.5 text-xs font-medium ${transitionStatusClass(notice.status)}`}
                      >
                        {notice.status}
                      </span>
                      <span className="font-medium text-slate-900">{notice.notice_number}</span>
                      <span className="text-slate-600"> — {notice.title}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}

          {activeTab === "changelog" ? (
            <div className="space-y-8">
              <p className="text-sm text-slate-600">
                Draft revision field updates, collection timeline events, and durable notes for this collection.
              </p>
              <section>
                <h3 className="text-sm font-semibold text-slate-900">Document revision changelog</h3>
                {detail.revisionChanges.length === 0 ? (
                  <p className="mt-2 text-sm text-slate-500">No structured changelog rows for documents in this collection.</p>
                ) : (
                  <ul className="mt-2 space-y-2 text-sm">
                    {detail.revisionChanges.map((change) => (
                      <li key={change.id} className="rounded-md border border-slate-100 bg-white p-3">
                        <div className="font-medium text-slate-900">{change.document_title}</div>
                        <div className="text-slate-700">
                          {formatRevisionChangeFieldLabel(change.field_name)}
                          {change.changed_from != null || change.changed_to != null ? (
                            <span className="text-slate-500">
                              {" "}
                              ({change.changed_from ?? "—"} → {change.changed_to ?? "—"})
                            </span>
                          ) : null}
                        </div>
                        {change.reason ? <div className="mt-1 text-slate-600">{change.reason}</div> : null}
                        <div className="mt-1 text-xs text-slate-500">{formatTimestamp(change.created_at)}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <h3 className="text-sm font-semibold text-slate-900">Collection timeline</h3>
                {detail.events.length === 0 ? (
                  <p className="mt-2 text-sm text-slate-500">No recorded collection events yet.</p>
                ) : (
                  <ul className="mt-2 space-y-2 text-sm">
                    {detail.events.map((event) => (
                      <li key={event.id} className="rounded-md border border-slate-100 bg-white p-3">
                        <div className="font-medium text-slate-900">{event.event_type}</div>
                        {event.event_note ? <div className="text-slate-600">{event.event_note}</div> : null}
                        <div className="mt-1 text-xs text-slate-500">{formatTimestamp(event.created_at)}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <h3 className="text-sm font-semibold text-slate-900">Notes</h3>
                {detail.notes.length === 0 ? (
                  <p className="mt-2 text-sm text-slate-500">No notes linked to this collection.</p>
                ) : (
                  <ul className="mt-2 space-y-2 text-sm">
                    {detail.notes.map((note) => (
                      <li key={note.id} className="rounded-md border border-slate-100 bg-white p-3">
                        <div className="text-slate-800">{note.body}</div>
                        <div className="mt-1 text-xs text-slate-500">{formatTimestamp(note.created_at)}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          ) : null}

          {activeTab === "related_collections_and_skus" ? (
            <div className="space-y-10">
              <section>
                <h3 className="text-sm font-semibold text-slate-900">Parent and child collections</h3>
                <p className="mt-1 text-sm text-slate-600">
                  Nesting uses <span className="font-mono">part_of</span> links (child collection is part of the parent).
                  Other relationship types may appear as legacy rows.
                </p>
                {detail.relationships.length === 0 ? (
                  <p className="mt-2 text-sm text-slate-500">No relationships recorded.</p>
                ) : (
                  <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                      <thead className="bg-slate-50">
                        <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                          <th className="px-3 py-2">Link</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 bg-white">
                        {detail.relationships.map((relation) => (
                          <tr key={relation.id}>
                            <td className="px-3 py-3 text-slate-800">
                              {formatHierarchyRelationship(collection.id, relation)}
                              {relation.relation_type !== "part_of" ? (
                                <span className="ml-2 text-xs text-amber-700">(legacy type)</span>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section>
                <h3 className="text-sm font-semibold text-slate-900">Member part numbers</h3>
                <p className="mt-1 text-sm text-slate-600">
                  Explicit members and any part numbers that currently use the linked BoM template (merged list).
                </p>
                {detail.skus.length === 0 ? (
                  <p className="mt-2 text-sm text-slate-500">No member part numbers yet.</p>
                ) : (
                  <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                      <thead className="bg-slate-50">
                        <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                          <th className="px-3 py-2">SKU</th>
                          <th className="px-3 py-2">Product name</th>
                          <th className="px-3 py-2">Membership</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 bg-white">
                        {detail.skus.map((row) => (
                          <tr key={row.sku}>
                            <td className="whitespace-nowrap px-3 py-3 font-medium">
                              <Link
                                className="text-brand-700 hover:underline"
                                to={buildMrpSkuDetailPath(row.sku)}
                              >
                                {row.sku}
                              </Link>
                            </td>
                            <td className="px-3 py-3 text-slate-700">{row.product_name ?? "—"}</td>
                            <td className="whitespace-nowrap px-3 py-3 text-slate-700">
                              {row.membership_source === "template" ? "BoM template" : "Explicit"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {detail.template_sku_exclusions.length > 0 ? (
                  <div className="mt-6 rounded-lg border border-amber-100 bg-amber-50/80 p-4">
                    <h4 className="text-sm font-semibold text-amber-950">Excluded from BoM template membership</h4>
                    <ul className="mt-2 list-inside list-disc text-sm text-slate-800">
                      {detail.template_sku_exclusions.map((row) => (
                        <li key={row.sku}>
                          <span className="font-mono">{row.sku}</span>
                          {row.product_name ? <span> — {row.product_name}</span> : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </section>
            </div>
          ) : null}

          {activeTab === "documents" ? (
            <div>
              <p className="mb-4 text-sm text-slate-600">Documents whose applicability includes this collection.</p>
              {sortedDocumentGroups.length === 0 ? (
                <p className="text-sm text-slate-500">No controlled documents for this collection.</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50">
                      <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                        <th className="px-3 py-2">Title</th>
                        <th className="px-3 py-2">Document type</th>
                        <th className="px-3 py-2">File type</th>
                        <th className="px-3 py-2">Active rev</th>
                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2" aria-label="Download" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 bg-white">
                      {sortedDocumentGroups.map((group) => {
                        const document = group.representative;
                        const listStatus = controlledDocumentListStatusSummary(group.revisions);
                        return (
                          <tr key={group.key}>
                            <td className="max-w-lg px-3 py-3 font-medium text-slate-900">{document.title}</td>
                            <td className="whitespace-nowrap px-3 py-3 text-slate-700">{document.document_kind_label}</td>
                            <td className="whitespace-nowrap px-3 py-3 tabular-nums text-slate-700">
                              {filenameExtension(document.original_filename) ?? "—"}
                            </td>
                            <td className="whitespace-nowrap px-3 py-3 text-slate-600">
                              {controlledDocumentListActiveRevisionLabel(group.revisions)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-3">
                              <span className={`rounded-full px-2 py-1 text-xs font-medium ${statusClass(listStatus)}`}>
                                {statusChipLabel(listStatus)}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 py-3">
                              <HoverHintTrigger
                                trigger={
                                  <button
                                    type="button"
                                    className="inline-flex rounded-md p-1 text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                                    aria-label={`Download ${document.title}`}
                                    onClick={() => void downloadControlledDocument(document)}
                                  >
                                    <Download className="h-4 w-4" aria-hidden />
                                  </button>
                                }
                              >
                                Download the stored file for this controlled document revision.
                              </HoverHintTrigger>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
